// RawMaterials.js - Admin page for M9 raw material (MP) stock: catalog,
// stock pool (available/reserved/threshold), movement
// history. Mirrors Stock.js's conventions (M7) for the finished-goods side.
import React, { useEffect, useRef, useState } from "react";
import "../Stock/Stock.css";
import { ToastContainer, toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { UilCheck } from "@iconscout/react-unicons";
import { userRequest } from "../../RequestMethods";
import { getThresholdError } from "../../utils/thresholdValidation";

const LAIZE_UNITS = ["cm", "m"];
const emptyNewMaterial = {
  name: "",
  composition: "",
  grammage: "",
  laize: "",
  laizeUnit: "cm",
  poids: "",
  initialQuantity: "",
  color: "",
  fournisseur: "",
  costPerUnit: "",
  minThreshold: "",
};

const formatNumber = (value) => (Number(value) > 0 ? String(Number(value)) : "—");

const RawMaterials = () => {
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMaterial, setNewMaterial] = useState(emptyNewMaterial);
  const [creating, setCreating] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  // Snapshot of the form when "Modifier" was clicked, to tell whether
  // anything was actually changed (Enregistrer stays locked until it was).
  const [editBaseline, setEditBaseline] = useState(null);
  const formSectionRef = useRef(null);
  const [thresholdDrafts, setThresholdDrafts] = useState({});
  const [applyingKeys, setApplyingKeys] = useState({});
  const [flashSuccessKeys, setFlashSuccessKeys] = useState({});

  // ── Movements - scoped to the selected raw material ──────────────────
  const [selectedMaterialId, setSelectedMaterialId] = useState("");
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementsPagination, setMovementsPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });

  const flashSuccess = (key) => {
    setFlashSuccessKeys((prev) => ({ ...prev, [key]: true }));
    setTimeout(() => {
      setFlashSuccessKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, 2000);
  };

  // silent: refresh the rows in place without swapping the table for the
  // spinner (used after a threshold save so the row doesn't flicker).
  const fetchRawMaterials = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await userRequest.get("raw-materials");
      setRawMaterials(res.data.rawMaterials || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des matières premières.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchMovements = async (materialId, page = 1) => {
    if (!materialId) {
      setMovements([]);
      return;
    }
    try {
      setMovementsLoading(true);
      const res = await userRequest.get(`raw-materials/${materialId}/movements`, { params: { page } });
      setMovements(res.data.movements || []);
      setMovementsPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des mouvements.");
    } finally {
      setMovementsLoading(false);
    }
  };

  useEffect(() => {
    fetchRawMaterials();
  }, []);

  const handleSelectMaterial = (id) => {
    setSelectedMaterialId(id);
    fetchMovements(id, 1);
  };

  // ── Edit form: threshold validation + "anything changed?" lock ───────────
  // Read the live row, not the snapshot taken on click, so a stock adjust
  // made meanwhile is reflected in the allowed range.
  const editingLive = editingMaterial
    ? rawMaterials.find((m) => m._id === editingMaterial._id) || editingMaterial
    : null;
  const editStockQty = editingLive?.stock?.availableQty ?? 0;
  const editStoredThreshold = editingLive?.stock?.minThreshold ?? 0;
  // An untouched or emptied field means "leave the threshold alone", so the
  // existing 0 on older materials never blocks saving the other fields.
  const thresholdChanged =
    Boolean(editingMaterial) &&
    newMaterial.minThreshold !== "" &&
    Number(newMaterial.minThreshold) !== editStoredThreshold;
  const thresholdFormError = thresholdChanged
    ? getThresholdError(newMaterial.minThreshold, editStockQty, { wholeUnits: false })
    : "";
  // The threshold counts as changed only through thresholdChanged (an emptied
  // field is "leave it alone", not an edit).
  const isEditDirty =
    Boolean(editBaseline) &&
    (thresholdChanged ||
      Object.keys(editBaseline).some((key) => key !== "minThreshold" && newMaterial[key] !== editBaseline[key]));

  const handleStartEdit = (material) => {
    const values = {
      name: material.name || "",
      composition: material.composition || "",
      grammage: Number(material.grammage) > 0 ? String(material.grammage) : "",
      laize: Number(material.laize) > 0 ? String(material.laize) : "",
      laizeUnit: material.laizeUnit || "cm",
      poids: Number(material.poids) > 0 ? String(material.poids) : "",
      initialQuantity: String(material.stock?.availableQty ?? 0),
      color: material.color || "",
      fournisseur: material.fournisseur || "",
      costPerUnit: Number(material.costPerUnit) > 0 ? String(material.costPerUnit) : "",
      minThreshold: String(material.stock?.minThreshold ?? 0),
    };
    setEditingMaterial(material);
    setEditBaseline(values);
    setNewMaterial(values);
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCancelEdit = () => {
    setEditingMaterial(null);
    setEditBaseline(null);
    setNewMaterial(emptyNewMaterial);
  };

  const handleSubmitMaterial = async (e) => {
    e.preventDefault();
    if (creating) return;
    if (!newMaterial.name.trim()) {
      toast.error("Nom requis.");
      return;
    }
    if (editingMaterial && thresholdFormError) {
      toast.error(thresholdFormError);
      return;
    }
    const fields = {
      name: newMaterial.name,
      composition: newMaterial.composition,
      grammage: Number(newMaterial.grammage) || 0,
      laize: Number(newMaterial.laize) || 0,
      laizeUnit: newMaterial.laizeUnit,
      poids: Number(newMaterial.poids) || 0,
      color: newMaterial.color,
      fournisseur: newMaterial.fournisseur,
      costPerUnit: Number(newMaterial.costPerUnit) || 0,
    };
    try {
      setCreating(true);
      if (editingMaterial) {
        // Stock quantity is not part of an edit - it only moves through the
        // stock ledger, so every change stays in the movement history.
        await userRequest.put(`raw-materials/${editingMaterial._id}`, fields);
        if (thresholdChanged) {
          await userRequest.patch(`raw-materials/${editingMaterial._id}/threshold`, {
            minThreshold: Number(newMaterial.minThreshold),
          });
        }
        toast.success("Matière première modifiée.");
        setEditingMaterial(null);
        setEditBaseline(null);
      } else {
        await userRequest.post("raw-materials", {
          ...fields,
          initialQuantity: Number(newMaterial.initialQuantity) || 0,
        });
        toast.success("Matière première créée.");
      }
      setNewMaterial(emptyNewMaterial);
      fetchRawMaterials();
    } catch (err) {
      const data = err?.response?.data;
      toast.error(data?.message || data?.error || (editingMaterial ? "Erreur lors de la modification." : "Erreur lors de la création."));
      // The fields may already be saved when only the threshold step failed.
      if (editingMaterial) fetchRawMaterials({ silent: true });
    } finally {
      setCreating(false);
    }
  };

  const handleThresholdChange = (materialId, value) => {
    setThresholdDrafts((prev) => ({ ...prev, [materialId]: value }));
  };

  const handleSaveThreshold = async (material) => {
    const value = thresholdDrafts[material._id];
    if (value === undefined || value === "") return;
    const key = `threshold-${material._id}`;
    if (applyingKeys[key]) return;
    const validationError = getThresholdError(value, material.stock?.availableQty ?? 0, { wholeUnits: false });
    if (validationError) {
      toast.error(validationError);
      return;
    }
    // Locked the instant the button is clicked, before the request starts,
    // so a second click can't send the same value twice.
    setApplyingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      await userRequest.patch(`raw-materials/${material._id}/threshold`, { minThreshold: Number(value) });
      toast.success("Seuil mis à jour.");
      flashSuccess(key);
      // Wait for the refreshed value, then drop the draft: the button stays
      // locked until the threshold is edited again.
      await fetchRawMaterials({ silent: true });
      setThresholdDrafts((prev) => {
        const next = { ...prev };
        delete next[material._id];
        return next;
      });
    } catch (err) {
      const data = err?.response?.data;
      toast.error(data?.message || data?.error || "Erreur lors de la mise à jour du seuil.");
    } finally {
      setApplyingKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  return (
    <div className="Stock">
      <h1>Matières premières</h1>

      <section className="stock-section" ref={formSectionRef}>
        <h2>
          {editingMaterial ? `Modifier la matière première : ${editingMaterial.name}` : "Ajouter une matière première"}
        </h2>
        <form className={`rm-form${editingMaterial ? " rm-form--editing" : ""}`} onSubmit={handleSubmitMaterial}>
          <label className="rm-field">
            <span>1. Nom</span>
            <input
              type="text" placeholder="ex: Tissu noir"
              value={newMaterial.name}
              onChange={(e) => setNewMaterial({ ...newMaterial, name: e.target.value })}
            />
          </label>
          <label className="rm-field">
            <span>2. Composition</span>
            <input
              type="text" placeholder="ex: 100% coton"
              value={newMaterial.composition}
              onChange={(e) => setNewMaterial({ ...newMaterial, composition: e.target.value })}
            />
          </label>
          <label className="rm-field">
            <span>3. Grammage (g/m²)</span>
            <input
              type="number" placeholder="ex: 180" min="0" step="1"
              value={newMaterial.grammage}
              onChange={(e) => setNewMaterial({ ...newMaterial, grammage: e.target.value })}
            />
          </label>
          <label className="rm-field">
            <span>4. Laize</span>
            <div className="rm-field-pair">
              <input
                type="number" placeholder="ex: 150" min="0" step="0.01"
                value={newMaterial.laize}
                onChange={(e) => setNewMaterial({ ...newMaterial, laize: e.target.value })}
              />
              <select
                value={newMaterial.laizeUnit}
                onChange={(e) => setNewMaterial({ ...newMaterial, laizeUnit: e.target.value })}
              >
                {LAIZE_UNITS.map((u) => <option key={u} value={u}>{u === "m" ? "mètre" : "centimètre"}</option>)}
              </select>
            </div>
          </label>
          <label className="rm-field">
            <span>5. Poids (KG)</span>
            <input
              type="number" placeholder="ex: 25" min="0" step="0.01"
              value={newMaterial.poids}
              onChange={(e) => setNewMaterial({ ...newMaterial, poids: e.target.value })}
            />
          </label>
          <label className="rm-field">
            <span>6. Métrage (m)</span>
            <input
              type="number" placeholder="ex: 200" min="0" step="0.01"
              value={newMaterial.initialQuantity}
              disabled={Boolean(editingMaterial)}
              title={editingMaterial ? "Le stock ne se modifie pas depuis ce formulaire" : undefined}
              onChange={(e) => setNewMaterial({ ...newMaterial, initialQuantity: e.target.value })}
            />
            {editingMaterial && <small className="rm-field-hint">Stock actuel (non modifiable ici).</small>}
          </label>
          <label className="rm-field">
            <span>7. Couleur</span>
            <input
              type="text" placeholder="ex: Noir"
              value={newMaterial.color}
              onChange={(e) => setNewMaterial({ ...newMaterial, color: e.target.value })}
            />
          </label>
          <label className="rm-field">
            <span>8. Fournisseur</span>
            <input
              type="text" placeholder="Nom du fournisseur"
              value={newMaterial.fournisseur}
              onChange={(e) => setNewMaterial({ ...newMaterial, fournisseur: e.target.value })}
            />
          </label>
          <label className="rm-field">
            <span>9. Prix (€ / KG)</span>
            <input
              type="number" placeholder="ex: 8.50" min="0" step="0.01"
              value={newMaterial.costPerUnit}
              onChange={(e) => setNewMaterial({ ...newMaterial, costPerUnit: e.target.value })}
            />
          </label>
          {editingMaterial && (
            <label className="rm-field">
              <span>10. Seuil d'alerte (m)</span>
              <input
                type="number" placeholder="ex: 20" min="0" step="0.01"
                className={thresholdFormError ? "rm-input--error" : ""}
                value={newMaterial.minThreshold}
                onChange={(e) => setNewMaterial({ ...newMaterial, minThreshold: e.target.value })}
              />
              <small className="rm-field-hint">
                {editStockQty > 0
                  ? `Stock : ${editStockQty} · seuil entre 0 et ${editStockQty} (exclus)`
                  : `Stock : ${editStockQty} · aucun seuil possible`}
              </small>
              {thresholdFormError && <small className="rm-field-error">{thresholdFormError}</small>}
            </label>
          )}
          <div className="rm-form-actions">
            {editingMaterial && (
              <button type="button" className="action-icon-btn btn-edit" onClick={handleCancelEdit} disabled={creating}>
                Annuler
              </button>
            )}
            <button
              type="submit"
              className="action-icon-btn btn-primary"
              disabled={creating || (Boolean(editingMaterial) && (!isEditDirty || Boolean(thresholdFormError)))}
              title={
                editingMaterial
                  ? (thresholdFormError || (!isEditDirty ? "Modifiez un champ pour enregistrer" : undefined))
                  : undefined
              }
            >
              {editingMaterial
                ? (creating ? "Enregistrement..." : "Enregistrer")
                : (creating ? "Création..." : "Ajouter")}
            </button>
          </div>
        </form>
      </section>

      <section className="stock-section">
        <h2>Stock des matières premières</h2>
        {loading ? (
          <div className="loading-container"><div className="loading-spinner-custom"></div></div>
        ) : (
          <TableContainer component={Paper} className="stock-table-container">
            <Table>
              <TableHead>
                <TableRow className="table-header">
                  <TableCell sx={{ fontWeight: "bold" }}>Nom</TableCell>
                  <TableCell>Composition</TableCell>
                  <TableCell>Grammage</TableCell>
                  <TableCell>Laize</TableCell>
                  <TableCell>Poids</TableCell>
                  <TableCell>Couleur</TableCell>
                  <TableCell>Fournisseur</TableCell>
                  <TableCell>Prix</TableCell>
                  <TableCell>Métrage disponible</TableCell>
                  <TableCell>Réservé</TableCell>
                  <TableCell>Seuil d'alerte</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rawMaterials.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center">Aucune matière première.</TableCell>
                  </TableRow>
                )}
                {rawMaterials.map((material) => {
                  return (
                    <TableRow key={material._id}>
                      <TableCell>{material.name}</TableCell>
                      <TableCell>{material.composition || "—"}</TableCell>
                      <TableCell>{Number(material.grammage) > 0 ? `${formatNumber(material.grammage)} g/m²` : "—"}</TableCell>
                      <TableCell>{Number(material.laize) > 0 ? `${formatNumber(material.laize)} ${material.laizeUnit || "cm"}` : "—"}</TableCell>
                      <TableCell>{Number(material.poids) > 0 ? `${formatNumber(material.poids)} kg` : "—"}</TableCell>
                      <TableCell>{material.color || "—"}</TableCell>
                      <TableCell>{material.fournisseur || "—"}</TableCell>
                      <TableCell>{Number(material.costPerUnit) > 0 ? `${formatNumber(material.costPerUnit)} € / kg` : "—"}</TableCell>
                      <TableCell>
                        {material.stock?.availableQty ?? 0} {material.unit}
                      </TableCell>
                      <TableCell>{material.stock?.reservedQty ?? 0} {material.unit}</TableCell>
                      <TableCell>
                        {(() => {
                          const stockQty = material.stock?.availableQty ?? 0;
                          const thresholdDraft = thresholdDrafts[material._id];
                          const thresholdError = getThresholdError(thresholdDraft, stockQty, { wholeUnits: false });
                          const thresholdKey = `threshold-${material._id}`;
                          const isSavingThreshold = Boolean(applyingKeys[thresholdKey]);
                          const hasChange = thresholdDraft !== undefined && thresholdDraft !== "" && Number(thresholdDraft) !== (material.stock?.minThreshold ?? 0);
                          const canSave = hasChange && !thresholdError && !isSavingThreshold;
                          return (
                            <div className="stock-threshold-wrap">
                              <div className="stock-threshold-cell">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className={thresholdError ? "stock-threshold-input--error" : ""}
                                  value={thresholdDraft ?? (material.stock?.minThreshold ?? 0)}
                                  onChange={(e) => handleThresholdChange(material._id, e.target.value)}
                                />
                                <button
                                  className={`action-icon-btn btn-edit${(isSavingThreshold || flashSuccessKeys[thresholdKey]) ? " flash-success" : ""}`}
                                  onClick={() => handleSaveThreshold(material)}
                                  disabled={!canSave}
                                  title={thresholdError || (hasChange ? "Enregistrer le seuil" : "Modifiez le seuil pour l'enregistrer")}
                                >
                                  <UilCheck />
                                </button>
                              </div>
                              <small className="stock-threshold-hint">
                                {stockQty > 0
                                  ? `Stock : ${stockQty} ${material.unit} · seuil entre 0 et ${stockQty} (exclus)`
                                  : `Stock : ${stockQty} ${material.unit} · aucun seuil possible`}
                              </small>
                              {thresholdError && <small className="stock-threshold-error">{thresholdError}</small>}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="stock-adjust-cell">
                          <button className="action-icon-btn btn-edit" onClick={() => handleStartEdit(material)}>
                            Modifier
                          </button>
                          <button className="action-icon-btn btn-edit" onClick={() => handleSelectMaterial(material._id)}>
                            Historique
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </section>

      {selectedMaterialId && (
        <section className="stock-section">
          <h2>Historique des mouvements</h2>
          {movementsLoading ? (
            <div className="loading-container"><div className="loading-spinner-custom"></div></div>
          ) : (
            <TableContainer component={Paper} className="stock-table-container">
              <Table>
                <TableHead>
                  <TableRow className="table-header">
                    <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Quantité</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Par</TableCell>
                    <TableCell>Raison</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">Aucun mouvement trouvé.</TableCell>
                    </TableRow>
                  )}
                  {movements.map((m) => (
                    <TableRow key={m._id}>
                      <TableCell>{new Date(m.createdAt).toLocaleString("fr-FR")}</TableCell>
                      <TableCell><span className={`badge movement-${m.type}`}>{m.type}</span></TableCell>
                      <TableCell>{m.quantity > 0 ? `+${m.quantity}` : m.quantity}</TableCell>
                      <TableCell>{m.sourceType}</TableCell>
                      <TableCell>{m.performedBy ? `${m.performedBy.name || ""} ${m.performedBy.surname || ""}`.trim() : "—"}</TableCell>
                      <TableCell>{m.reason || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </section>
      )}

      <ToastContainer position="top-right" />
    </div>
  );
};

export default RawMaterials;
